import { DOMAIN_MESSAGES } from './domain-messages';
import type { Locale } from './messages';

/**
 * Questions de cadrage posées à la prise de rendez-vous (POC §9A.2) : peu nombreuses, à choix, pour
 * préparer l'entretien sans le remplacer. Chaque réponse devient un fait **déclaré** par le client
 * (`epistemic_status: declared`), jamais confirmé ni déduit : l'entretien le confirmera. Les clés et
 * valeurs ne dépendent pas de la langue ; seuls les libellés se traduisent (domain-messages).
 */

export type ScopeKey = 'target_domains' | 'current_systems' | 'industry' | 'site_count_band' | 'user_count_band';

export interface ScopeQuestion {
  key: ScopeKey;
  domain: string;
  kind: 'multi' | 'single';
  options: readonly string[];
  required?: boolean;
}

/** Noms de produits : identiques dans toutes les langues. */
const PRODUCTS: Readonly<Record<string, string>> = { odoo: 'Odoo', sellsy: 'Sellsy', pennylane: 'Pennylane', shopify: 'Shopify' };

export const SCOPE_QUESTIONS: readonly ScopeQuestion[] = [
  { key: 'target_domains', domain: 'business_scope', kind: 'multi', required: true, options: ['sales', 'purchasing', 'inventory', 'production', 'quality', 'traceability', 'accounting', 'integrations'] },
  { key: 'current_systems', domain: 'integrations', kind: 'multi', options: ['odoo', 'sellsy', 'pennylane', 'shopify', 'spreadsheet', 'custom', 'paper', 'other'] },
  { key: 'industry', domain: 'business_scope', kind: 'single', options: ['food', 'manufacturing', 'distribution', 'textile', 'other'] },
  { key: 'site_count_band', domain: 'organization', kind: 'single', options: ['1', '2-3', '4+'] },
  { key: 'user_count_band', domain: 'organization', kind: 'single', options: ['1-5', '6-20', '21-50', '50+'] },
];

export function questionLabel(key: ScopeKey, locale: Locale): string {
  return DOMAIN_MESSAGES[locale].questions[key].label;
}

export function questionHelp(key: ScopeKey, locale: Locale): string | undefined {
  const question = DOMAIN_MESSAGES[locale].questions[key];
  return 'help' in question ? question.help : undefined;
}

export function optionLabel(key: ScopeKey, value: string, locale: Locale): string {
  const options = DOMAIN_MESSAGES[locale].questions[key].options as Readonly<Record<string, string>>;
  return PRODUCTS[value] ?? options[value] ?? value;
}

export type ScopeAnswers = Readonly<Record<string, readonly string[]>>;

/** Fait déclaré à la prise de rendez-vous : aperçu de la forme `discovery-fact` (plan §2 C2, §13). */
export interface DeclaredFact {
  domain: string;
  key: string;
  value: string | readonly string[];
  epistemic_status: 'declared';
  validation_status: 'unvalidated';
  source_kind: 'customer_declaration';
  /** Langue dans laquelle le client a répondu : un texte libre n'est pas traduit. */
  locale: Locale;
}

export function declaredFacts(answers: ScopeAnswers, objective: string, locale: Locale, otherSystem = ''): DeclaredFact[] {
  const facts: DeclaredFact[] = [];
  const base = { epistemic_status: 'declared', validation_status: 'unvalidated', source_kind: 'customer_declaration', locale } as const;
  for (const question of SCOPE_QUESTIONS) {
    let values = [...(answers[question.key] ?? [])];
    if (question.key === 'current_systems' && values.includes('other') && otherSystem.trim() !== '') values = values.map((value) => (value === 'other' ? `other:${otherSystem.trim()}` : value));
    if (values.length === 0) continue;
    facts.push({ ...base, domain: question.domain, key: `${question.domain}.${question.key}`, value: question.kind === 'single' ? values[0]! : values });
  }
  const trimmed = objective.trim();
  if (trimmed !== '') facts.push({ ...base, domain: 'business_scope', key: 'business_scope.primary_objective', value: trimmed });
  return facts;
}

/** Libellé d'un fait déclaré dans la langue d'affichage (la valeur stockée ne change pas). */
export function factLabel(fact: DeclaredFact, locale: Locale): string | null {
  const key = fact.key.split('.')[1] as ScopeKey;
  return SCOPE_QUESTIONS.some((question) => question.key === key) ? questionLabel(key, locale) : null;
}

export function factDisplay(fact: DeclaredFact, locale: Locale, separator: string): string {
  const key = fact.key.split('.')[1] as ScopeKey;
  if (!SCOPE_QUESTIONS.some((question) => question.key === key)) return String(fact.value);
  const values = Array.isArray(fact.value) ? fact.value : [fact.value as string];
  return values.map((value) => (value.startsWith('other:') ? value.slice(6) : optionLabel(key, value, locale))).join(separator);
}

/** Libellés des domaines visés, dans l'ordre des options : pour le récapitulatif. */
export function domainLabels(answers: ScopeAnswers, locale: Locale): string[] {
  const chosen = new Set(answers['target_domains'] ?? []);
  return SCOPE_QUESTIONS[0]!.options.filter((value) => chosen.has(value)).map((value) => optionLabel('target_domains', value, locale));
}
